import { type IPostCandidatesSearchBody, type TAdditionalFilters, cleanObject } from "@api";
import type { AdditionalFilterValues } from "@bbj-types/candidate";
import { isArrayOf, isCandidateGender } from "@root/utils/typeguards";
import equal from "fast-deep-equal/react";
import { useReducer } from "react";
import {
  type IAdditionalStaticFilters,
  resolverAdditionalFiltersDelete,
  resolverAdditionalFiltersUpdate,
} from "./additionalFilters/logics";
import type { IAddParticipantFilters, TAddParticipantFiltersAction } from "./types";

const initialFiltersState = {
  search: "",
  prescribers: [],
  authors: [],
  coachingSteps: [],
  status: [],
  additionalFilters: {},
  place: [],
  teams: [],
} as const satisfies IAddParticipantFilters;

const filtersReducer = (
  state: IAddParticipantFilters,
  action: TAddParticipantFiltersAction
): IAddParticipantFilters => {
  switch (action.type) {
    case "search":
    case "authors":
    case "coachingSteps":
    case "status":
    case "prescribers":
    case "place":
    case "teams":
    case "additionalFilters":
      if (equal(state[action.type], action.payload)) {
        return state;
      }
      return { ...state, [action.type]: action.payload };
    case "additionalFilters:update":
      return resolverAdditionalFiltersUpdate(state, action.payload.field, action.payload.value);
    case "additionalFilters:delete":
      return resolverAdditionalFiltersDelete(state, action.payload);
    case "extendedFilters:delete":
      return { ...state, [action.payload]: initialFiltersState[action.payload] };
    case "reset":
      return initialFiltersState;
    default:
      return state;
  }
};

export const useAddParticipantFilters = () => {
  const [filters, dispatch] = useReducer(filtersReducer, initialFiltersState);

  return { filters, dispatch };
};

const cleanAdditionalFilters = (filters: Record<string, AdditionalFilterValues | null>) => {
  const additionalSections: TAdditionalFilters = {};
  const additionalStaticFilters: Partial<IAdditionalStaticFilters> = {};
  for (const key in filters) {
    const value = filters[key];
    switch (key) {
      case "gender": {
        if (isArrayOf(value, isCandidateGender)) {
          additionalStaticFilters.gender = value;
        }
        break;
      }
      case "birthDate": {
        if (value && typeof value === "object" && "range" in value) {
          additionalStaticFilters.birthDate = value.range;
        }
        break;
      }
      case "objectives": {
        if (Array.isArray(value)) {
          additionalStaticFilters.objectives = value;
        }
        break;
      }
      default: {
        if (value !== null) {
          if (typeof value === "object" && "range" in value) {
            const range = value.range;
            if (range.startDate || range.endDate) {
              additionalSections[key] = {
                startDate: range.startDate,
                endDate: range.endDate,
              };
            }
          } else {
            additionalSections[key] = value;
          }
        }
      }
    }
  }
  return [additionalSections, additionalStaticFilters] as const;
};

export const formatSendFilterSearchCandidate = (filters: IAddParticipantFilters): IPostCandidatesSearchBody => {
  const [additionalSections, additionalStaticFilters] = cleanAdditionalFilters(filters.additionalFilters);
  return cleanObject({
    ...additionalStaticFilters,
    additionalSections: additionalSections,
    source: filters.prescribers.map((p) => p.value),
    authors: filters.authors.map((a) => a._id),
    coachingStep: filters.coachingSteps.map((cs) => cs.value),
    status: filters.status.map((s) => s.value),
    multiSearch: filters.search,
    team: filters.teams.map((team) => team._id),
    place: filters.place.map((el) => el._id),
    isArchived: false,
    isAnonymized: false,
    isDeleted: false,
  });
};
